import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogFooter,
    DialogOverlay,
    DialogPortal,
    DialogTitle
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Textarea } from '@/components/ui/textarea';
import * as DialogPrimitive from "@radix-ui/react-dialog";
import React, { memo, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import { TagSelector } from '@/components/tags/tag-selector';
import { MultiSelect } from '@/components/ui/multi-select';
import { cn } from '@/lib/utils';
import { useProjectEntities } from '@/services/query/entities-queries';
import { useAddEntityToPlotcard, usePlotcardEntities, useRemoveEntityFromPlotcard } from '@/services/query/plotcard-entities-queries';
import { Entity } from '@/services/supabase/entities';
import { Copy, Loader2, Save, Trash2, X } from 'lucide-react';
import type { PlotCard } from '../types';

// Custom DialogContent without the automatic close button
const DialogContent = React.forwardRef<
    React.ElementRef<typeof DialogPrimitive.Content>,
    React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>
>(({ className, children, ...props }, ref) => (
    <DialogPortal>
        <DialogOverlay />
        <DialogPrimitive.Content
            ref={ref}
            className={cn(
                "fixed left-[50%] top-[50%] z-50 grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border border-border/60 bg-background/95 backdrop-blur-xl p-6 shadow-2xl duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-2xl",
                className
            )}
            {...props}
        >
            {children}
        </DialogPrimitive.Content>
    </DialogPortal>
));
DialogContent.displayName = "DialogContent";

// Define card colors
const cardColors: Record<string, { bg: string, border: string, text: string }> = {
    plot: {
        bg: "bg-blue-100/80 dark:bg-blue-950/50",
        border: "border-blue-200 dark:border-blue-800",
        text: "text-blue-900 dark:text-blue-100"
    },
    character: {
        bg: "bg-emerald-100/80 dark:bg-emerald-950/50",
        border: "border-emerald-200 dark:border-emerald-800",
        text: "text-emerald-900 dark:text-emerald-100"
    },
    setting: {
        bg: "bg-violet-100/80 dark:bg-violet-950/50",
        border: "border-violet-200 dark:border-violet-800",
        text: "text-violet-900 dark:text-violet-100"
    },
    conflict: {
        bg: "bg-rose-100/80 dark:bg-rose-950/50",
        border: "border-rose-200 dark:border-rose-800",
        text: "text-rose-900 dark:text-rose-100"
    },
    theme: {
        bg: "bg-amber-100/80 dark:bg-amber-950/50",
        border: "border-amber-200 dark:border-amber-800",
        text: "text-amber-900 dark:text-amber-100"
    },
    note: {
        bg: "bg-slate-100/80 dark:bg-slate-800/50",
        border: "border-slate-200 dark:border-slate-700",
        text: "text-slate-900 dark:text-slate-100"
    },
    "ai-context-prompt": {
        bg: "bg-teal-100/80 dark:bg-teal-950/50",
        border: "border-teal-200 dark:border-teal-800",
        text: "text-teal-900 dark:text-teal-100"
    }
};

// Import the theme-specific styles
import '@/styles/plotcard-themes.css';

// Helper function to get card styling safely
const getCardStyle = (type?: string) => {
    // Return the styling for the specified type if it exists, otherwise default to 'note'
    return cardColors[type && type in cardColors ? type : 'note'];
};

interface PlotCardDetailsModalProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    card: PlotCard | null;
    onSave?: (card: PlotCard, updates: Partial<PlotCard>) => Promise<void>;
    onDelete?: (card: PlotCard) => void;
    onDuplicate?: (card: PlotCard) => void;
}

const PlotCardDetailsModalComponent: React.FC<PlotCardDetailsModalProps> = ({
    open,
    onOpenChange,
    card,
    onSave,
    onDelete,
    onDuplicate
}) => {
    if (!card) return null;

    // State for form data and saving
    const [isSaving, setIsSaving] = useState(false);
    const [formData, setFormData] = useState({
        title: '',
        content: '',
        type: 'note'
    });

    // Fetch entities attached to this plotcard
    const { data: attachedEntities = [], isLoading: isLoadingEntities } = usePlotcardEntities(card.id);
    const addEntityMutation = useAddEntityToPlotcard();
    const removeEntityMutation = useRemoveEntityFromPlotcard();

    const { projectId } = useParams();
    const { data: allEntities = [] } = useProjectEntities(projectId);

    // Local optimistic state for entities
    const [localEntities, setLocalEntities] = useState<Entity[]>([]);

    // Sync local state with server data
    useEffect(() => {
        if (attachedEntities) {
            setLocalEntities(attachedEntities);
        }
    }, [attachedEntities]);

    // Reset form data when card changes
    useEffect(() => {
        if (card) {
            setFormData({
                title: card.title || '',
                content: card.content || '',
                type: card.type || 'note'
            });
        }
    }, [card]);

    // Handle input changes
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // Handle type selection
    const handleTypeChange = (value: string) => {
        setFormData(prev => ({
            ...prev,
            type: value
        }));
    };

    // Handle save
    const handleSave = async () => {
        if (!onSave || !card) return;

        try {
            setIsSaving(true);
            await onSave(card, {
                title: formData.title,
                content: formData.content,
                type: formData.type as any
            });
            onOpenChange(false);
        } catch (error) {
            console.error('Error saving card:', error);
        } finally {
            setIsSaving(false);
        }
    };



    const cardStyle = getCardStyle(formData.type);

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-2xl bg-background p-0 overflow-hidden">
                {/* Header with title and close button */}
                <div className="p-6 pb-4 border-b border-border/40 bg-muted/5">
                    <div className="flex justify-between items-center">
                        <DialogTitle className="text-lg font-medium">Update card</DialogTitle>
                        <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 rounded-full"
                            onClick={() => onOpenChange(false)}
                        >
                            <X className="h-4 w-4" />
                        </Button>
                    </div>

                    {/* Card identifier and location */}
                    <div className="mt-3">
                        <div className="text-sm font-medium text-foreground/90">
                            <span className={cn("mr-2", cardStyle.text)}>
                                {formData.type.toUpperCase()}-{card.id}
                            </span>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-muted-foreground mt-1 font-medium opacity-80">
                            <span>{formData.title || card.title}</span>
                            <span>•</span>
                            <span>{(card as any).plotline_name || 'Unknown timeline'}</span>
                            {(card as any).chapter_name && (
                                <>
                                    <span>•</span>
                                    <span>{(card as any).chapter_name}</span>
                                </>
                            )}
                        </div>
                    </div>
                </div>

                {/* Main content area */}
                <div className="p-5">
                    {/* Title and Type in a row */}
                    <div className="grid grid-cols-3 gap-4 mb-5">
                        {/* Title field */}
                        <div className="col-span-2">
                            <Label htmlFor="title" className="text-sm text-muted-foreground block mb-2 font-medium">Title</Label>
                            <Input
                                id="title"
                                name="title"
                                value={formData.title}
                                onChange={handleInputChange}
                                placeholder="Card title"
                                className="h-10 bg-muted/30 border-muted-foreground/10 focus:border-primary/30 rounded-xl shadow-sm"
                            />
                        </div>

                        {/* Type selection */}
                        <div>
                            <Label htmlFor="type" className="text-sm text-muted-foreground block mb-2 font-medium">Type</Label>
                            <Select value={formData.type} onValueChange={handleTypeChange}>
                                <SelectTrigger id="type" className="h-10 bg-muted/30 border-muted-foreground/10 focus:border-primary/30 rounded-xl shadow-sm">
                                    <SelectValue placeholder="Select type" />
                                </SelectTrigger>
                                <SelectContent className="rounded-xl border-border/60 shadow-xl">
                                    <SelectItem value="plot" className="rounded-lg cursor-pointer">
                                        <div className="flex items-center gap-2">
                                            <div className="w-2 h-2 rounded-full bg-blue-500" />
                                            <span>Plot</span>
                                        </div>
                                    </SelectItem>
                                    <SelectItem value="character">
                                        <div className="flex items-center gap-2">
                                            <div className="w-2 h-2 rounded-full bg-emerald-500" />
                                            <span>Character</span>
                                        </div>
                                    </SelectItem>
                                    <SelectItem value="setting">
                                        <div className="flex items-center gap-2">
                                            <div className="w-2 h-2 rounded-full bg-violet-500" />
                                            <span>Setting</span>
                                        </div>
                                    </SelectItem>
                                    <SelectItem value="conflict">
                                        <div className="flex items-center gap-2">
                                            <div className="w-2 h-2 rounded-full bg-rose-500" />
                                            <span>Conflict</span>
                                        </div>
                                    </SelectItem>
                                    <SelectItem value="theme">
                                        <div className="flex items-center gap-2">
                                            <div className="w-2 h-2 rounded-full bg-amber-500" />
                                            <span>Theme</span>
                                        </div>
                                    </SelectItem>
                                    <SelectItem value="note">
                                        <div className="flex items-center gap-2">
                                            <div className="w-2 h-2 rounded-full bg-slate-500" />
                                            <span>Note</span>
                                        </div>
                                    </SelectItem>
                                    <SelectItem value="ai-context-prompt">
                                        <div className="flex items-center gap-2">
                                            <div className="w-2 h-2 rounded-full bg-teal-500" />
                                            <span>AI Context Prompt</span>
                                        </div>
                                    </SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>

                    {/* Content field */}
                    <div className="mb-6">
                        <Label htmlFor="content" className="text-sm text-muted-foreground block mb-2 font-medium">Content</Label>
                        <Textarea
                            id="content"
                            name="content"
                            value={formData.content}
                            onChange={handleInputChange}
                            className="min-h-[120px] resize-none bg-muted/30 border-muted-foreground/10 focus:border-primary/30 rounded-xl shadow-sm"
                            placeholder="Card content"
                        />
                    </div>

                    {/* Tags Section */}
                    <div className="mb-5">
                        <Label className="text-sm text-muted-foreground block mb-2">Tags</Label>
                        {projectId && card.id && (
                            <TagSelector
                                projectId={projectId}
                                plotcardId={card.id}
                            />
                        )}
                    </div>

                    <Separator className="my-5" />

                    {/* Entities Section */}
                    <div className="mb-5">
                        <Label className="text-sm text-muted-foreground block mb-2">Entities</Label>
                        <MultiSelect
                            options={allEntities.map(e => ({ label: e.name, value: e.id }))}
                            selected={localEntities.map(e => e.id)}
                            onChange={(newSelectedIds) => {
                                const currentIds = localEntities.map(e => e.id);
                                const addedId = newSelectedIds.find(id => !currentIds.includes(id as number));
                                const removedId = currentIds.find(id => !newSelectedIds.includes(id));

                                if (addedId) {
                                    const entityIdx = allEntities.findIndex(e => e.id === addedId);
                                    if (entityIdx !== -1) {
                                        const entity = allEntities[entityIdx];
                                        // Optimistic add
                                        setLocalEntities(prev => [...prev, entity]);

                                        addEntityMutation.mutate({
                                            plotcardId: card.id,
                                            entityId: entity.id,
                                            projectId: projectId || ''
                                        }, {
                                            onError: () => {
                                                // Revert on error
                                                setLocalEntities(prev => prev.filter(e => e.id !== entity.id));
                                            }
                                        });
                                    }
                                }
                                if (removedId) {
                                    const entityToRemove = localEntities.find(e => e.id === removedId);
                                    if (entityToRemove) {
                                        // Optimistic remove
                                        setLocalEntities(prev => prev.filter(e => e.id !== removedId));

                                        removeEntityMutation.mutate({
                                            plotcardId: card.id,
                                            entityId: removedId,
                                            projectId: projectId || ''
                                        }, {
                                            onError: () => {
                                                // Revert on error
                                                setLocalEntities(prev => [...prev, entityToRemove]);
                                            }
                                        });
                                    }
                                }
                            }}
                            placeholder="Search and select entities..."
                            emptyText="No entities found"
                            isLoading={isLoadingEntities}
                            className="min-h-11 rounded-xl border-muted-foreground/10 bg-muted/30 focus-within:ring-primary/20 hover:bg-muted/40 transition-colors shadow-sm"
                            badgeClassName="rounded-lg bg-background/50 hover:bg-background/80 border border-border/10"
                        />
                    </div>

                </div>

                <DialogFooter className="p-6 pt-4 border-t border-border/40 bg-muted/5 gap-2">
                    {onDelete && (
                        <Button
                            variant="ghost"
                            onClick={() => {
                                onDelete(card);
                                onOpenChange(false);
                            }}
                            disabled={isSaving}
                            size="lg"
                            className="bg-destructive/10 text-destructive hover:bg-destructive/20 hover:text-destructive rounded-xl"
                        >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete
                        </Button>
                    )}
                    {onDuplicate && (
                        <Button
                            variant="outline"
                            onClick={() => {
                                onDuplicate(card);
                                onOpenChange(false);
                            }}
                            disabled={isSaving}
                            size="lg"
                            className="rounded-xl hover:bg-muted/50"
                        >
                            <Copy className="h-4 w-4 mr-2" />
                            Duplicate
                        </Button>
                    )}
                    {/* Cancel button removed - using close button at the top instead */}
                    <Button
                        onClick={handleSave}
                        disabled={isSaving}
                        size="lg"
                        className="px-6 rounded-xl shadow-lg shadow-primary/20"
                    >
                        {isSaving ? (
                            <>
                                <Loader2 className="animate-spin mr-2 h-4 w-4" />
                                Saving...
                            </>
                        ) : (
                            <>
                                <Save className="h-4 w-4 mr-2" />
                                Save Changes
                            </>
                        )}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog >
    );
};

// Export a memoized version of the component to fix the static flag warning
export const PlotCardDetailsModal = memo(PlotCardDetailsModalComponent);